'use client';

import { useEffect, useRef } from 'react';
import useWebSocketStore from '@/lib/hooks/zustand-stores/use-websocket-store';
import { useLogger } from '@/lib/hooks/useLogger';

interface UseWebSocketReconnectOptions {
  /**
   * Whether to enable automatic reconnection
   * @default true
   */
  enabled?: boolean;

  /**
   * Callback to run on reconnection attempt
   */
  onReconnect?: () => void;

  /**
   * Callback to run when reconnection succeeds
   */
  onReconnectSuccess?: () => void;

  /**
   * Callback to run when reconnection fails
   */
  onReconnectFail?: (error: unknown) => void;

  /**
   * Whether to enable debug logging
   * @default process.env.NODE_ENV === 'development'
   */
  debug?: boolean;
}

/**
 * Hook to handle WebSocket reconnection when a user returns to the tab or app
 * 
 * This hook adds a visibility change listener to detect when a user returns to the tab
 * and attempts to reconnect the WebSocket if it's disconnected.
 * 
 * @example
 * // Basic usage
 * useWebSocketReconnect();
 * 
 * @example
 * // With callbacks
 * useWebSocketReconnect({
 *   onReconnect: () => console.log('Attempting reconnect...'),
 *   onReconnectSuccess: () => console.log('Reconnected successfully!'),
 *   onReconnectFail: (error) => console.error('Failed to reconnect', error)
 * });
 */
export default function useWebSocketReconnect(options: UseWebSocketReconnectOptions = {}) {
  const {
    enabled = true,
    onReconnect,
    onReconnectSuccess,
    onReconnectFail,
    debug = process.env.NODE_ENV === 'development'
  } = options;

  const logger = useLogger();
  const wasHidden = useRef(false);
  
  // Get WebSocket store data
  const connectionStatus = useWebSocketStore(state => state.connectionStatus);
  const reconnect = useWebSocketStore(state => state.reconnect);

  useEffect(() => {
    // Skip if disabled
    if (!enabled) return;

    const log = (message: string) => {
      if (debug) {
        logger.debug(`[useWebSocketReconnect] ${message}`);
      }
    };

    const handleVisibilityChange = async () => {
      const isHidden = document.visibilityState === 'hidden';
      
      // Track when the page becomes hidden
      if (isHidden) {
        wasHidden.current = true;
        log('Page hidden, tracking state for potential reconnection');
        return;
      }

      // If page was previously hidden and now visible again, check the connection
      if (wasHidden.current && connectionStatus !== 'connected') {
        log(`User returned to tab, WebSocket is ${connectionStatus}`);
        
        // Only attempt reconnection if the connection is not currently connecting
        if (connectionStatus !== 'connecting') {
          try {
            log('Attempting WebSocket reconnection');
            if (onReconnect) onReconnect();
            
            await reconnect();
            
            log('WebSocket reconnection successful');
            if (onReconnectSuccess) onReconnectSuccess();
          } catch (error) {
            log(`WebSocket reconnection failed: ${error}`);
            if (onReconnectFail) onReconnectFail(error);
          }
        }
      }
      
      // Reset flag
      wasHidden.current = false;
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Add listener for user returning to the app after app is in background (mobile)
    if (typeof window !== 'undefined' && 'addEventListener' in window) {
      window.addEventListener('focus', () => {
        if (connectionStatus !== 'connected' && connectionStatus !== 'connecting') {
          log('Window focus detected, checking WebSocket connection');
          handleVisibilityChange();
        }
      });
    }

    // Remove listeners on cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (typeof window !== 'undefined' && 'removeEventListener' in window) {
        window.removeEventListener('focus', handleVisibilityChange);
      }
    };
  }, [connectionStatus, reconnect, enabled, debug, logger, onReconnect, onReconnectSuccess, onReconnectFail]);

  return {
    reconnect,
    connectionStatus
  };
} 